import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  Users, Shield, Store, Settings, Clock, Wallet, Award, Save, Plus, Trash2, Key, 
  Lock, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, MapPin, Search, Download, Trophy, 
  UserPlus, Globe, Receipt, X, LayoutDashboard, ShoppingCart, Package, Layers, Truck, ShoppingBag, ArrowRightLeft, BarChart3, Sparkles, Database, RefreshCw, Upload
} from 'lucide-react';

export default function Admin() {
  const { user: currentUser } = useContext(AuthContext);
  const { settings: globalSettings, updateSettings } = useSettings();
  const [activeTab, setActiveTab] = useState('users');
  const [settings, setSettings] = useState(globalSettings);

  // Sync local state when global settings load
  useEffect(() => {
    setSettings(globalSettings);
  }, [globalSettings]);  // Users State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('cashier');
  const [storeId, setStoreId] = useState('1');
  const [success, setSuccess] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);

  // Stores State
  const [stores, setStores] = useState([]);
  const [storeName, setStoreName] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [editingStore, setEditingStore] = useState(null);

  // Password Change State
  const [pwdData, setPwdData] = useState({ old: '', new: '', confirm: '' });

  // 2FA Setup State
  const [is2faModalOpen, setIs2faModalOpen] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  
  // Roles State
  const [roles, setRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [expandedRole, setExpandedRole] = useState(null);

  const AVAILABLE_PERMISSIONS = [
    { id: 'dashboard', label: 'View Dashboard', icon: LayoutDashboard },
    { id: 'pos', label: 'Sales Terminal', icon: ShoppingCart },
    { id: 'products', label: 'Manage Products', icon: Package },
    { id: 'inventory', label: 'Manage Inventory', icon: Layers },
    { id: 'vendors', label: 'Manage Vendors', icon: Truck },
    { id: 'purchases', label: 'Manage Purchases', icon: ShoppingBag },
    { id: 'transfers', label: 'Stock Transfers', icon: ArrowRightLeft },
    { id: 'reports', label: 'View Reports', icon: BarChart3 },
    { id: 'marketing', label: 'Manage Marketing', icon: Sparkles },
    { id: 'customers', label: 'Manage Customers', icon: Users },
    { id: 'settings', label: 'System Settings', icon: Settings },
    { id: 'giftcards', label: 'Gift Cards', icon: Wallet }
  ];

  // Gift Cards State
  const [giftCards, setGiftCards] = useState([]);
  const [newGiftCardCode, setNewGiftCardCode] = useState('');
  const [newGiftCardBalance, setNewGiftCardBalance] = useState('');

  const loadGiftCards = () => {
    api.get('/giftcards').then(res => setGiftCards(res.data)).catch(console.error);
  };

  // Attendance State
  const [attendanceLogs, setAttendanceLogs] = useState([]);

  const loadUsers = () => {
    api.get('/auth/users').then(res => setUsers(res.data)).catch(console.error);
  };

  const loadStores = () => {
    api.get('/stores').then(res => setStores(res.data)).catch(console.error);
  };

  const loadRoles = () => {
    api.get('/roles').then(res => setRoles(res.data)).catch(console.error);
  };

  const loadAttendance = () => {
    api.get('/attendance/logs').then(res => setAttendanceLogs(res.data)).catch(console.error);
  };

  useEffect(() => { 
    loadUsers(); 
    loadStores();
    loadAttendance();
    loadRoles();
    loadGiftCards();
  }, []);

  const handleEditUser = (u) => {
    setEditingUser(u);
    setName(u.name || '');
    setEmail(u.email);
    setRole(u.role);
    setStoreId(String(u.storeId));
    setPassword('');
    setActiveTab('users');
  };

  const cancelUserEdit = () => {
    setEditingUser(null);
    setName('');
    setEmail('');
    setRole('cashier');
    setStoreId('1');
    setPassword('');
  };

  const register = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/auth/users/${editingUser.id}`, { email, name, role, storeId: parseInt(storeId) });
        setEditingUser(null);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        await api.post('/auth/register', { email, password, name, role, storeId: parseInt(storeId) });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
      setEmail('');
      setPassword('');
      setName('');
      loadUsers();
    } catch (err) {
      console.error(err);
      alert('Error saving user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleResetPassword = async (u) => {
    const newPwd = prompt(`Enter a new temporary password for ${u.name || u.email}:`);
    if (newPwd) {
      if (newPwd.length < 6) return alert('Password must be at least 6 characters.');
      try {
        await api.post(`/auth/users/${u.id}/reset-password`, { newPassword: newPwd });
        alert('Password reset successfully!');
      } catch (err) {
        alert('Error resetting password');
      }
    }
  };

  const handleUpdateRolePermissions = async (roleId, permissions) => {
    try {
      await api.put(`/roles/${roleId}`, { permissions });
      loadRoles();
    } catch (err) {
      alert('Error updating permissions');
    }
  };

  const togglePermission = (role, permId) => {
    const currentPerms = JSON.parse(role.permissions || '[]');
    const newPerms = currentPerms.includes(permId)
      ? currentPerms.filter(p => p !== permId)
      : [...currentPerms, permId];
    handleUpdateRolePermissions(role.id, newPerms);
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    try {
      await api.post('/roles', { name: newRoleName });
      setNewRoleName('');
      loadRoles();
    } catch (err) {
      alert(err.response?.data?.error || 'Error adding role');
    }
  };

  const handleDeleteRole = async (id) => {
    if (window.confirm('Delete this role?')) {
      try {
        await api.delete(`/roles/${id}`);
        loadRoles();
      } catch (err) {
        alert(err.response?.data?.error || 'Error deleting role');
      }
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to remove this staff member?')) {
      try {
        await api.delete(`/auth/users/${id}`);
        loadUsers();
      } catch (err) {
        console.error(err);
        alert('Error deleting user');
      }
    }
  };

  const handleEditStore = (store) => {
    setEditingStore(store);
    setStoreName(store.name);
    setStoreAddress(store.address || '');
    setActiveTab('stores');
  };

  const cancelStoreEdit = () => {
    setEditingStore(null);
    setStoreName('');
    setStoreAddress('');
  };

  const createStore = async (e) => {
    e.preventDefault();
    try {
      if (editingStore) {
        await api.put(`/stores/${editingStore.id}`, { name: storeName, address: storeAddress });
        setEditingStore(null);
        alert('Store updated successfully!');
      } else {
        await api.post('/stores', { name: storeName, address: storeAddress });
        alert('Store created successfully!');
      }
      setStoreName('');
      setStoreAddress('');
      loadStores();
    } catch (err) {
      console.error(err);
      alert('Error saving store: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteStore = async (id) => {
    if (window.confirm('Are you sure you want to remove this store? This will fail if there are active staff or data linked to it.')) {
      try {
        await api.delete(`/stores/${id}`);
        loadStores();
      } catch (err) {
        console.error(err);
        alert('Error deleting store: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const baseUrl = api.defaults.baseURL.replace('/api', '');
      const fullUrl = `${baseUrl}${res.data.url}`;
      handleUpdateSettings({ store_logo: fullUrl });
    } catch (err) {
      console.error(err);
      alert('Error uploading logo');
    }
  };

  const handleResetColors = async () => {
    if (!window.confirm("Are you sure you want to reset all theme colors to default?")) return;
    const resetFields = {
      brand_color: '',
      theme_bg_main: '',
      theme_sidebar: '',
      theme_text_main: ''
    };
    try {
      await updateSettings(resetFields);
      // Immediately remove CSS variables to show effect
      document.documentElement.style.removeProperty('--brand');
      document.documentElement.style.removeProperty('--bg-main');
      document.documentElement.style.removeProperty('--sidebar');
      document.documentElement.style.removeProperty('--text-main');
    } catch(err) {
      console.error(err);
      alert("Error resetting colors");
    }
  };

  const handleUpdateSettings = async (updates) => {
    try {
      console.log('Updating Settings with:', updates);
      await updateSettings(updates);
    } catch (err) {
      console.error(err);
      alert('Error updating settings: ' + (err.response?.data?.error || err.message));
    }
  };

  const saveSettings = async () => {
    try {
      await api.post('/settings', settings);
      alert('Settings updated successfully!');
    } catch (err) {
      console.error(err);
      alert('Error updating settings: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdData.new !== pwdData.confirm) return alert('New passwords do not match');
    try {
      await api.post('/auth/change-password', {
        userId: currentUser.id,
        oldPassword: pwdData.old,
        newPassword: pwdData.new
      });
      setPwdData({ old: '', new: '', confirm: '' });
      alert('Password updated successfully!');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Error updating password');
    }
  };

  const handleSetup2FA = async () => {
    try {
      const res = await api.post('/auth/2fa/generate');
      setQrCode(res.data.qrCodeUrl);
      setIs2faModalOpen(true);
    } catch (err) {
      console.error(err);
      alert('Error generating 2FA secret');
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/2fa/verify', { code: twoFactorCode });
      setIs2faModalOpen(false);
      setTwoFactorCode('');
      alert('Two-Factor Authentication enabled successfully!');
      loadUsers(); 
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Invalid verification code');
    }
  };

  const handleDisable2FA = async () => {
    if (window.confirm('Are you sure you want to disable 2FA? This will reduce your account security.')) {
      try {
        await api.post('/auth/2fa/disable');
        alert('2FA disabled successfully');
        loadUsers();
      } catch (err) {
        console.error(err);
        alert('Error disabling 2FA');
      }
    }
  };

  const handleBackup = async () => {
    try {
      const response = await api.get('/admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nexus_pos_backup_${new Date().toISOString().split('T')[0]}.db`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Backup failed');
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm('WARNING: This will replace your current database with the selected backup. All existing data will be overwritten. Continue?')) {
      e.target.value = '';
      return;
    }
    
    const formData = new FormData();
    formData.append('database', file);
    
    try {
      await api.post('/admin/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Database restored successfully! The page will now reload.');
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Restore failed');
      e.target.value = '';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <div className="space-y-8 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight">
                    <UserPlus size={24} className="text-brand" /> {editingUser ? 'Edit Staff Member' : 'Create New Staff'}
                  </h3>
                  <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">
                    {editingUser ? `Modifying profile for ${editingUser.name || editingUser.email}` : 'Add a new staff member and assign their role.'}
                  </p>
                </div>
                {editingUser && (
                  <span className="bg-brand/10 text-brand px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest animate-pulse">Editing Mode</span>
                )}
              </div>

              {success && (
                <div className="mb-6 bg-emerald-500/10 text-emerald-500 p-4 rounded-xl text-xs font-black border border-emerald-500/20 flex items-center gap-2 uppercase tracking-widest">
                  <CheckCircle2 size={18} /> Staff member {editingUser ? 'updated' : 'created'} successfully.
                </div>
              )}

              <form onSubmit={register} className="space-y-5 max-w-2xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Full Name</label>
                    <input required type="text" placeholder="e.g. Jane Smith" value={name} onChange={e => setName(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Email Address</label>
                    <input required type="email" placeholder="staff@example.com" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-sm" />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {!editingUser && (
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Temporary Password</label>
                      <input required type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-sm" />
                    </div>
                  )}
                  <div className={editingUser ? 'sm:col-span-2' : ''}>
                    <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">System Role</label>
                    <select value={role} onChange={e => setRole(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-sm uppercase font-black text-[10px] tracking-widest">
                      {roles.length === 0 ? (
                        <>
                          <option value="cashier">Cashier</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Administrator</option>
                        </>
                      ) : (
                        roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)
                      )}
                    </select>
                  </div>
                </div>

                <div className="pt-4 flex gap-3">
                  <button type="submit" className="bg-brand hover:bg-brand-light text-white px-8 py-3 rounded-xl font-black transition-all shadow-lg flex items-center gap-2 uppercase text-xs tracking-widest">
                    <Save size={18} /> {editingUser ? 'Update Account' : 'Create Account'}
                  </button>
                  {editingUser && (
                    <button type="button" onClick={cancelUserEdit} className="bg-theme-card border border-theme text-theme-muted px-8 py-3 rounded-xl font-black transition-all hover:bg-theme-main uppercase text-xs tracking-widest">
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme overflow-hidden">
              <div className="p-6 border-b border-theme bg-theme-main/10">
                <h3 className="text-sm font-black text-theme-muted uppercase tracking-widest flex items-center gap-2">
                  <Users size={18} className="text-brand" /> Current Staff Members
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Store ID</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-theme">
                    {users.map(u => (
                      <tr key={u.id} className="hover:bg-theme-main/10 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand font-black border border-brand/20 shadow-sm">
                              {(u.name || u.email).charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-bold text-theme-main">{u.name || 'Unnamed Staff'}</div>
                              <div className="text-[10px] text-theme-muted font-mono">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                            u.role === 'admin' ? 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/20' :
                            u.role === 'manager' ? 'bg-brand/10 text-brand border border-brand/20' :
                            'bg-theme-main text-theme-muted border border-theme'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-theme-muted text-xs font-mono">#{u.storeId}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            <button onClick={() => handleResetPassword(u)} className="p-1.5 text-theme-muted hover:text-amber-500 hover:bg-amber-500/10 rounded-lg transition-all" title="Reset Password">
                              <Key size={18} />
                            </button>
                            <button onClick={() => handleEditUser(u)} className="p-1.5 text-theme-muted hover:text-brand hover:bg-brand/10 rounded-lg transition-all" title="Edit Staff">
                              <Settings size={18} />
                            </button>
                            {u.role !== 'admin' && (
                              <button onClick={() => handleDeleteUser(u.id)} className="p-1.5 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all" title="Delete Staff">
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'roles':
        return (
          <div className="space-y-8 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6">
                <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight"><Shield size={24} className="text-brand" /> Manage Staff Roles</h3>
                <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">Define custom roles and permissions for your team.</p>
              </div>
              <form onSubmit={handleAddRole} className="flex gap-4 max-w-lg">
                <div className="flex-1">
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Role Name</label>
                  <input required type="text" placeholder="e.g. Inventory Manager" value={newRoleName} onChange={e => setNewRoleName(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-sm" />
                </div>
                <div className="self-end">
                  <button type="submit" className="bg-brand hover:bg-brand-light text-white px-6 py-2.5 rounded-xl font-black transition-all shadow-lg flex items-center gap-2 uppercase text-[10px] tracking-widest">
                    <Plus size={18} /> Add Role
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme overflow-hidden">
              <div className="p-6 border-b border-theme bg-theme-main/10">
                <h3 className="text-sm font-black text-theme-muted uppercase tracking-widest flex items-center gap-2">
                  <Shield size={18} className="text-brand" /> Role Configuration & Permissions
                </h3>
              </div>
              <div className="divide-y divide-theme">
                {roles.length === 0 && !roles.find(r => r.name === 'admin') ? (
                  <div className="p-10 text-center">
                    <p className="text-theme-muted text-xs font-bold uppercase tracking-widest">No custom roles defined yet.</p>
                  </div>
                ) : (
                  <>
                    {/* Hardcoded System Admin Display */}
                    <div className="transition-all border-b border-theme bg-brand/5">
                      <div 
                        onClick={() => setExpandedRole(expandedRole === 'system-admin' ? null : 'system-admin')}
                        className="p-6 flex justify-between items-center cursor-pointer hover:bg-brand/10 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center font-black uppercase text-xs shadow-lg shadow-brand/20">
                            A
                          </div>
                          <div>
                            <h4 className="font-black text-brand uppercase tracking-tight flex items-center gap-2">Administrator <span className="bg-brand text-white text-[8px] px-2 py-0.5 rounded-full">System</span></h4>
                            <p className="text-[10px] text-theme-muted font-black uppercase tracking-widest mt-0.5">Master Access • Permanent Role</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Lock size={16} className="text-brand opacity-50" />
                          {expandedRole === 'system-admin' ? <ChevronUp size={20} className="text-brand" /> : <ChevronDown size={20} className="text-brand" />}
                        </div>
                      </div>
                      {expandedRole === 'system-admin' && (
                        <div className="px-6 pb-8 pt-2 bg-brand/5 border-t border-brand/10 animate-fade-in">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {AVAILABLE_PERMISSIONS.map(p => {
                              const Icon = p.icon;
                              return (
                                <div key={p.id} className="flex items-center gap-3 p-4 rounded-2xl border bg-brand/10 border-brand/30 text-brand shadow-sm opacity-80 cursor-default">
                                  <Icon size={18} />
                                  <span className="text-[10px] font-black uppercase tracking-widest">{p.label}</span>
                                  <CheckCircle2 size={14} className="ml-auto" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {roles.filter(r => r.name !== 'admin').map(r => {
                      const isExpanded = expandedRole === r.id;
                      const perms = JSON.parse(r.permissions || '[]');
                      
                      return (
                        <div key={r.id} className="transition-all">
                          <div 
                            onClick={() => setExpandedRole(isExpanded ? null : r.id)}
                            className="p-6 flex justify-between items-center cursor-pointer hover:bg-theme-main/10 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-theme-main border border-theme flex items-center justify-center text-theme-main font-black uppercase text-xs">
                                {r.name.charAt(0)}
                              </div>
                              <div>
                                <h4 className="font-black text-theme-main uppercase tracking-tight">{r.name}</h4>
                                <p className="text-[10px] text-theme-muted font-black uppercase tracking-widest mt-0.5">{perms.length} Permissions Active</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleDeleteRole(r.id); }}
                                className="p-2 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all"
                              >
                                <Trash2 size={16} />
                              </button>
                              {isExpanded ? <ChevronUp size={20} className="text-theme-muted" /> : <ChevronDown size={20} className="text-theme-muted" />}
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="px-6 pb-8 pt-2 bg-theme-main/5 border-t border-theme animate-fade-in">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {AVAILABLE_PERMISSIONS.map(p => {
                                  const Icon = p.icon;
                                  const isActive = perms.includes(p.id);
                                  return (
                                    <label 
                                      key={p.id}
                                      className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${
                                        isActive 
                                          ? 'bg-brand/10 border-brand/30 text-brand shadow-sm' 
                                          : 'bg-theme-card border-theme text-theme-muted hover:border-theme-muted/50'
                                      }`}
                                    >
                                      <input 
                                        type="checkbox"
                                        className="sr-only"
                                        checked={isActive}
                                        onChange={() => togglePermission(r, p.id)}
                                      />
                                      <Icon size={18} />
                                      <span className="text-[10px] font-black uppercase tracking-widest">{p.label}</span>
                                      {isActive && <CheckCircle2 size={14} className="ml-auto" />}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case 'stores':
        return (
          <div className="space-y-8 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight">
                    <Store size={24} className="text-brand" /> {editingStore ? 'Edit Store Details' : 'Create Store Location'}
                  </h3>
                  <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">
                    {editingStore ? `Modifying settings for ${editingStore.name}` : 'Register a new store branch for the enterprise.'}
                  </p>
                </div>
                {editingStore && (
                  <span className="bg-brand/10 text-brand px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest animate-pulse">Editing Mode</span>
                )}
              </div>
              <form onSubmit={createStore} className="space-y-5 max-w-2xl">
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Store Name</label>
                  <input required type="text" placeholder="e.g. Downtown Branch" value={storeName} onChange={e => setStoreName(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Address</label>
                  <input required type="text" placeholder="123 Commerce St." value={storeAddress} onChange={e => setStoreAddress(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-sm" />
                </div>
                <div className="pt-4 flex gap-3">
                  <button type="submit" className="bg-brand hover:bg-brand-light text-white px-8 py-3 rounded-xl font-black transition-all shadow-lg flex items-center gap-2 uppercase text-xs tracking-widest">
                    <Save size={18} /> {editingStore ? 'Update Store' : 'Register Store'}
                  </button>
                  {editingStore && (
                    <button type="button" onClick={cancelStoreEdit} className="bg-theme-card border border-theme text-theme-muted px-8 py-3 rounded-xl font-black transition-all hover:bg-theme-main uppercase text-xs tracking-widest">
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme overflow-hidden">
              <div className="p-6 border-b border-theme bg-theme-main/10">
                <h3 className="text-sm font-black text-theme-muted uppercase tracking-widest flex items-center gap-2">
                  <MapPin size={18} className="text-brand" /> Active Store Locations
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                      <th className="px-6 py-4">Store ID</th>
                      <th className="px-6 py-4">Name</th>
                      <th className="px-6 py-4">Address</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-theme">
                    {stores.map(s => (
                      <tr key={s.id} className="hover:bg-theme-main/10 transition-colors">
                        <td className="px-6 py-4 font-mono text-theme-muted text-xs">#{s.id}</td>
                        <td className="px-6 py-4 font-bold text-theme-main">{s.name}</td>
                        <td className="px-6 py-4 text-theme-muted text-sm">{s.address}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => handleEditStore(s)} className="p-2 text-theme-muted hover:text-brand hover:bg-brand/10 rounded-lg transition-all" title="Edit Store">
                              <Settings size={16} />
                            </button>
                            <button onClick={() => handleDeleteStore(s.id)} className="p-2 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all" title="Delete Store">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'security':
        const isGlobal2faEnabled = settings.two_factor_enabled === 'true';
        const me = users.find(u => u.id === currentUser.id);
        const my2fa = me?.isTwoFactorEnabled;

        return (
          <div className="space-y-8 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6">
                <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight"><Shield size={24} className="text-brand" /> Security Policies</h3>
                <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">Manage global security settings and authentication rules.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-6 rounded-2xl border flex flex-col gap-4 transition-all ${isGlobal2faEnabled ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-amber-500/10 border-amber-500/20 text-amber-500'}`}>
                  <div className="flex gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${isGlobal2faEnabled ? 'bg-emerald-500/20 text-emerald-500' : 'bg-amber-500/20 text-amber-500'}`}>
                      {isGlobal2faEnabled ? <CheckCircle2 size={32} /> : <AlertTriangle size={32} />}
                    </div>
                    <div>
                      <p className="font-black uppercase tracking-tight text-lg">Global 2FA Policy</p>
                      <p className="text-xs font-bold uppercase tracking-widest opacity-80 mt-1">
                        {isGlobal2faEnabled ? 'Enforced for all accounts.' : 'Currently optional.'}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleUpdateSettings({ two_factor_enabled: isGlobal2faEnabled ? 'false' : 'true' })} className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-lg mt-2 ${isGlobal2faEnabled ? 'bg-theme-card text-emerald-500 border border-emerald-500/20 hover:bg-emerald-500/10' : 'bg-theme-main text-amber-500 border border-amber-500/20 hover:bg-amber-500/10'}`}>
                    {isGlobal2faEnabled ? 'Disable Global Policy' : 'Enable Global Policy'}
                  </button>
                </div>

                <div className={`p-6 rounded-2xl border flex flex-col gap-4 transition-all ${my2fa ? 'bg-brand/10 border-brand/20 text-brand' : 'bg-theme-main/40 border-theme text-theme-muted'}`}>
                  <div className="flex gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${my2fa ? 'bg-brand/20 text-brand' : 'bg-theme-main text-theme-muted border border-theme'}`}>
                      <Key size={32} />
                    </div>
                    <div>
                      <p className="font-black uppercase tracking-tight text-lg">Personal 2FA Status</p>
                      <p className="text-xs font-bold uppercase tracking-widest opacity-80 mt-1">
                        {my2fa ? 'Your account is secured.' : 'Add extra security layer.'}
                      </p>
                    </div>
                  </div>
                  {my2fa ? (
                    <button onClick={handleDisable2FA} className="w-full py-3 bg-theme-card text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-rose-500/10 transition-all shadow-lg mt-2">
                      Disable My 2FA
                    </button>
                  ) : (
                    <button onClick={handleSetup2FA} className="w-full py-3 bg-brand text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-brand-light transition-all shadow-lg mt-2 flex items-center justify-center gap-2">
                      Connect Authenticator
                    </button>
                  )}
                </div>
              </div>
            </div>

            {is2faModalOpen && (
              <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in">
                <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-theme">
                  <div className="px-6 py-4 border-b border-theme flex justify-between items-center bg-theme-main/10">
                    <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Setup Authenticator</h3>
                    <button onClick={() => setIs2faModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-lg transition-colors"><X size={24}/></button>
                  </div>
                  <div className="p-8 text-center">
                    <p className="text-xs font-bold text-theme-muted uppercase tracking-widest mb-8">Scan this QR code with your app and enter the 6-digit code below.</p>
                    <div className="bg-white p-6 inline-block rounded-2xl border-4 border-theme shadow-2xl mb-8">
                      <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 mx-auto" />
                    </div>
                    <form onSubmit={handleVerify2FA} className="space-y-6">
                      <div>
                        <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-3 text-left">Verification Code</label>
                        <input required type="text" maxLength="6" placeholder="000000" value={twoFactorCode} onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, ''))} className="w-full text-center text-3xl tracking-[0.5em] font-black bg-theme-main border border-theme rounded-2xl py-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all shadow-inner" />
                      </div>
                      <div className="flex gap-4 pt-2">
                        <button type="button" onClick={() => setIs2faModalOpen(false)} className="flex-1 py-4 bg-theme-card border border-theme text-theme-muted rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-theme-main transition-all">Cancel</button>
                        <button type="submit" className="flex-1 py-4 bg-brand hover:bg-brand-light text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg transition-all">Verify & Activate</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6">
                <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight"><Key size={24} className="text-brand" /> Change My Password</h3>
                <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">Update your personal account credentials.</p>
              </div>
              <form onSubmit={handleChangePassword} className="space-y-5 max-w-lg">
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Current Password</label>
                  <input required type="password" value={pwdData.old} onChange={e => setPwdData({...pwdData, old: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">New Password</label>
                  <input required type="password" value={pwdData.new} onChange={e => setPwdData({...pwdData, new: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Confirm New Password</label>
                  <input required type="password" value={pwdData.confirm} onChange={e => setPwdData({...pwdData, confirm: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all" />
                </div>
                <div className="pt-4">
                  <button type="submit" className="bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-xl font-black transition-all shadow-lg flex items-center gap-2 uppercase text-xs tracking-widest">
                    <Save size={18} /> Update Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8 animate-fade-in-up">
            <div className="border-b border-theme pb-5 mb-6">
              <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight"><Settings size={24} className="text-brand" /> Branding & Preferences</h3>
              <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">Configure your store's identity and global settings.</p>
            </div>
            <div className="space-y-8 max-w-2xl">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2 flex items-center gap-2"><Globe size={14} /> Base Currency</label>
                  <CurrencySearch 
                    value={settings.base_currency}
                    onChange={(c) => {
                      setSettings({...settings, base_currency: c.code.toLowerCase(), currency_symbol: c.symbol});
                      handleUpdateSettings({ base_currency: c.code.toLowerCase(), currency_symbol: c.symbol });
                    }}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2 flex items-center gap-2"><Globe size={14} /> Currency Symbol</label>
                  <input 
                    type="text" 
                    maxLength="5"
                    value={settings.currency_symbol || '$'} 
                    onChange={e => setSettings({...settings, currency_symbol: e.target.value})}
                    onBlur={() => handleUpdateSettings({ currency_symbol: settings.currency_symbol })}
                    className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2 flex items-center gap-2"><Receipt size={14} /> Global Tax Rate (%)</label>
                  <input 
                    type="number" 
                    step="0.1"
                    min="0"
                    value={settings.tax_rate} 
                    onChange={e => setSettings({...settings, tax_rate: e.target.value})}
                    onBlur={() => handleUpdateSettings({ tax_rate: settings.tax_rate })}
                    className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2 flex items-center gap-2"><MapPin size={14} /> Timezone</label>
                  <select 
                    value={settings.timezone} 
                    onChange={e => handleUpdateSettings({ timezone: e.target.value })}
                    className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold"
                  >
                    <option value="UTC">UTC (Universal)</option>
                    <option value="EST">EST (Eastern)</option>
                    <option value="WAT">WAT (West Africa)</option>
                  </select>
                </div>
              </div>

              <div className="pt-8 border-t border-theme">
                <h4 className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-6">Receipt Customization</h4>
                <div className="space-y-6">
                  <div>
                    <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Store / Platform Name</label>
                    <input 
                      type="text" 
                      value={settings.store_name || ''}
                      onChange={e => setSettings({...settings, store_name: e.target.value})}
                      onBlur={() => handleUpdateSettings({ store_name: settings.store_name })}
                      className="w-full bg-theme-main border border-theme rounded-xl py-3 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold" 
                      placeholder="e.g. Nexus Market"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Brand Theme Color (Accent)</label>
                      <div className="flex gap-2">
                        <input 
                          type="color" 
                          value={settings.brand_color || '#6366f1'}
                          onChange={e => setSettings({...settings, brand_color: e.target.value})}
                          onBlur={() => handleUpdateSettings({ brand_color: settings.brand_color })}
                          className="h-10 w-12 bg-theme-main border border-theme rounded-lg p-0.5 cursor-pointer" 
                        />
                        <input type="text" value={settings.brand_color || '#6366f1'} onChange={e => setSettings({...settings, brand_color: e.target.value})} onBlur={() => handleUpdateSettings({ brand_color: settings.brand_color })} className="flex-1 bg-theme-main border border-theme rounded-lg py-2 px-3 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold uppercase text-xs" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Main Background</label>
                      <div className="flex gap-2">
                        <input type="color" value={settings.theme_bg_main || '#f8fafc'} onChange={e => setSettings({...settings, theme_bg_main: e.target.value})} onBlur={() => handleUpdateSettings({ theme_bg_main: settings.theme_bg_main })} className="h-10 w-12 bg-theme-main border border-theme rounded-lg p-0.5 cursor-pointer" />
                        <input type="text" value={settings.theme_bg_main || '#f8fafc'} onChange={e => setSettings({...settings, theme_bg_main: e.target.value})} onBlur={() => handleUpdateSettings({ theme_bg_main: settings.theme_bg_main })} className="flex-1 bg-theme-main border border-theme rounded-lg py-2 px-3 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold uppercase text-xs" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Sidebar Background</label>
                      <div className="flex gap-2">
                        <input type="color" value={settings.theme_sidebar || '#ffffff'} onChange={e => setSettings({...settings, theme_sidebar: e.target.value})} onBlur={() => handleUpdateSettings({ theme_sidebar: settings.theme_sidebar })} className="h-10 w-12 bg-theme-main border border-theme rounded-lg p-0.5 cursor-pointer" />
                        <input type="text" value={settings.theme_sidebar || '#ffffff'} onChange={e => setSettings({...settings, theme_sidebar: e.target.value})} onBlur={() => handleUpdateSettings({ theme_sidebar: settings.theme_sidebar })} className="flex-1 bg-theme-main border border-theme rounded-lg py-2 px-3 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold uppercase text-xs" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Primary Text Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={settings.theme_text_main || '#1e293b'} onChange={e => setSettings({...settings, theme_text_main: e.target.value})} onBlur={() => handleUpdateSettings({ theme_text_main: settings.theme_text_main })} className="h-10 w-12 bg-theme-main border border-theme rounded-lg p-0.5 cursor-pointer" />
                        <input type="text" value={settings.theme_text_main || '#1e293b'} onChange={e => setSettings({...settings, theme_text_main: e.target.value})} onBlur={() => handleUpdateSettings({ theme_text_main: settings.theme_text_main })} className="flex-1 bg-theme-main border border-theme rounded-lg py-2 px-3 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold uppercase text-xs" />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" onClick={handleResetColors} className="text-[10px] font-black text-rose-500 hover:bg-rose-500/10 px-3 py-1.5 rounded-lg transition-colors uppercase tracking-widest">
                      Reset Colors to Default
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Upload Store Logo</label>
                      <div className="flex gap-4 items-center">
                        <input 
                          type="file" 
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="flex-1 bg-theme-main border border-theme rounded-xl py-2 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all text-xs file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-black file:bg-brand file:text-white hover:file:bg-brand-light" 
                        />
                        {settings.store_logo && (
                          <div className="w-12 h-12 border-2 border-theme rounded-xl overflow-hidden bg-white shrink-0 shadow-lg p-1 relative group">
                            <img src={settings.store_logo} alt="Logo" className="w-full h-full object-contain" />
                            <button onClick={() => {setSettings({...settings, store_logo: ''}); handleUpdateSettings({store_logo: ''});}} className="absolute inset-0 bg-rose-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove Logo"><X size={16} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Display Mode</label>
                      <select
                        value={settings.logo_display_mode || 'both'}
                        onChange={e => {
                          setSettings({...settings, logo_display_mode: e.target.value});
                          handleUpdateSettings({ logo_display_mode: e.target.value });
                        }}
                        className="w-full bg-theme-main border border-theme rounded-xl py-3 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold text-sm"
                      >
                        <option value="both">Logo & Title</option>
                        <option value="logo">Logo Only</option>
                        <option value="title">Title Only</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Receipt Footer Text</label>
                    <textarea 
                      rows="3" 
                      value={settings.receipt_footer}
                      onChange={e => setSettings({...settings, receipt_footer: e.target.value})}
                      onBlur={() => handleUpdateSettings({ receipt_footer: settings.receipt_footer })}
                      className="w-full bg-theme-main border border-theme rounded-xl py-3 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-medium" 
                      placeholder="Thank you for shopping with us!"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'attendance':
        return (
          <div className="space-y-6 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme overflow-hidden">
              <div className="p-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
                <h3 className="text-sm font-black text-theme-muted uppercase tracking-widest flex items-center gap-2">
                  <Clock size={18} className="text-brand" /> Staff Attendance Logs
                </h3>
                <button onClick={loadAttendance} className="text-brand text-xs font-bold hover:underline">Refresh</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                      <th className="px-6 py-4">Staff Member</th>
                      <th className="px-6 py-4">Clock In</th>
                      <th className="px-6 py-4">Clock Out</th>
                      <th className="px-6 py-4">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-theme">
                    {attendanceLogs.map(log => {
                      const start = new Date(log.clockIn);
                      const end = log.clockOut ? new Date(log.clockOut) : null;
                      let duration = 'Active';
                      if (end) {
                        const diff = end - start;
                        const h = Math.floor(diff / 3600000);
                        const m = Math.floor((diff % 3600000) / 60000);
                        duration = `${h}h ${m}m`;
                      }
                      return (
                        <tr key={log.id} className="hover:bg-theme-main/10 transition-colors group">
                          <td className="px-6 py-4">
                            <div className="font-bold text-theme-main">{log.user.name}</div>
                            <div className="text-[10px] text-theme-muted uppercase font-black">{log.user.role}</div>
                          </td>
                          <td className="px-6 py-4 text-xs text-theme-main font-medium">{start.toLocaleString()}</td>
                          <td className="px-6 py-4 text-xs text-theme-main font-medium">{end ? end.toLocaleString() : '-'}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                              duration === 'Active' ? 'bg-emerald-500/10 text-emerald-500 animate-pulse' : 'bg-theme-main text-theme-muted'
                            }`}>
                              {duration}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {attendanceLogs.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-10 text-center text-theme-muted italic">No attendance records found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'loyalty':
        return (
          <div className="space-y-8 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6">
                <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight">
                  <Award size={24} className="text-brand" /> Loyalty Program Configuration
                </h3>
                <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">
                  Define how customers earn points, unlock tiers, and redeem rewards.
                </p>
              </div>

              {/* Point Redemption */}
              <div className="mb-8">
                <h4 className="text-xs font-black text-theme-muted uppercase tracking-widest mb-4">Point Redemption Value</h4>
                <div className="bg-theme-main/10 rounded-2xl p-6 border border-theme">
                  <div className="max-w-sm">
                    <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Cash Value per Point ($)</label>
                    <input
                      type="number" step="0.001" min="0"
                      value={settings.points_conversion_rate}
                      onChange={e => setSettings({...settings, points_conversion_rate: e.target.value})}
                      className="w-full bg-theme-main border border-theme rounded-xl py-3 px-5 text-theme-main font-black outline-none focus:ring-2 focus:ring-brand"
                    />
                    <p className="text-[10px] text-theme-muted mt-2 italic">
                      At {settings.points_conversion_rate}, a customer needs {Math.round(1 / (parseFloat(settings.points_conversion_rate) || 0.01))} points for $1.00 discount.
                    </p>
                  </div>
                </div>
              </div>

              {/* Tier Thresholds */}
              <div className="mb-8">
                <h4 className="text-xs font-black text-theme-muted uppercase tracking-widest mb-4">Tier Upgrade Thresholds (Lifetime Spend)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-orange-500/5 rounded-2xl p-6 border border-orange-500/20">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-lg">🥉</span>
                      <span className="text-xs font-black text-orange-500 uppercase tracking-widest">Bronze</span>
                    </div>
                    <p className="text-[10px] text-theme-muted font-bold mb-1">Threshold: $0 (Default)</p>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-1 mt-3">Points Multiplier</label>
                      <input
                        type="number" step="0.1" min="0"
                        value={settings.loyalty_bronze_multiplier}
                        onChange={e => setSettings({...settings, loyalty_bronze_multiplier: e.target.value})}
                        className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-black outline-none focus:ring-2 focus:ring-orange-500 text-center text-lg"
                      />
                      <p className="text-[10px] text-theme-muted mt-1 text-center">{settings.loyalty_bronze_multiplier}x points per $1</p>
                    </div>
                  </div>

                  <div className="bg-indigo-500/5 rounded-2xl p-6 border border-indigo-500/20">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-lg">🥈</span>
                      <span className="text-xs font-black text-indigo-500 uppercase tracking-widest">Silver</span>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-1">Spend Threshold ($)</label>
                      <input
                        type="number" min="0"
                        value={settings.loyalty_silver_threshold}
                        onChange={e => setSettings({...settings, loyalty_silver_threshold: e.target.value})}
                        className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-black outline-none focus:ring-2 focus:ring-indigo-500 text-center"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-1 mt-3">Points Multiplier</label>
                      <input
                        type="number" step="0.1" min="0"
                        value={settings.loyalty_silver_multiplier}
                        onChange={e => setSettings({...settings, loyalty_silver_multiplier: e.target.value})}
                        className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-black outline-none focus:ring-2 focus:ring-indigo-500 text-center text-lg"
                      />
                      <p className="text-[10px] text-theme-muted mt-1 text-center">{settings.loyalty_silver_multiplier}x points per $1</p>
                    </div>
                  </div>

                  <div className="bg-amber-500/5 rounded-2xl p-6 border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-lg">🥇</span>
                      <span className="text-xs font-black text-amber-600 uppercase tracking-widest">Gold</span>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-1">Spend Threshold ($)</label>
                      <input
                        type="number" min="0"
                        value={settings.loyalty_gold_threshold}
                        onChange={e => setSettings({...settings, loyalty_gold_threshold: e.target.value})}
                        className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-black outline-none focus:ring-2 focus:ring-amber-500 text-center"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-1 mt-3">Points Multiplier</label>
                      <input
                        type="number" step="0.1" min="0"
                        value={settings.loyalty_gold_multiplier}
                        onChange={e => setSettings({...settings, loyalty_gold_multiplier: e.target.value})}
                        className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-black outline-none focus:ring-2 focus:ring-amber-500 text-center text-lg"
                      />
                      <p className="text-[10px] text-theme-muted mt-1 text-center">{settings.loyalty_gold_multiplier}x points per $1</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={saveSettings}
                className="bg-brand hover:bg-brand-light text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg shadow-brand/20"
              >
                <Save size={18} /> Save Loyalty Settings
              </button>
            </div>
          </div>
        );
      case 'giftcards':
        return (
          <div className="space-y-8 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6">
                <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight">
                  <Wallet size={24} className="text-brand" /> Issue Gift Card
                </h3>
                <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">Generate a new redeemable code.</p>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post('/giftcards', { code: newGiftCardCode, balance: newGiftCardBalance });
                  setNewGiftCardCode('');
                  setNewGiftCardBalance('');
                  loadGiftCards();
                  alert('Gift card generated!');
                } catch (err) {
                  alert(err.response?.data?.error || 'Error generating gift card');
                }
              }} className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Code</label>
                  <input required placeholder="e.g. SUMMER50" value={newGiftCardCode} onChange={e => setNewGiftCardCode(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-bold outline-none uppercase" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Initial Balance</label>
                  <input required type="number" step="0.01" min="0" placeholder="50.00" value={newGiftCardBalance} onChange={e => setNewGiftCardBalance(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-bold outline-none" />
                </div>
                <div className="flex items-end">
                  <button type="submit" className="w-full bg-brand hover:bg-brand-light text-white px-4 py-2.5 rounded-xl font-black uppercase text-xs tracking-widest shadow-md">
                    Generate
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme overflow-hidden">
              <div className="p-6 border-b border-theme bg-theme-main/10">
                <h3 className="text-sm font-black text-theme-muted uppercase tracking-widest">Active & Past Gift Cards</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                      <th className="px-6 py-4">Code</th>
                      <th className="px-6 py-4">Balance</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Created Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-theme">
                    {giftCards.map(gc => (
                      <tr key={gc.id} className="hover:bg-theme-main/10 transition-colors">
                        <td className="px-6 py-4 font-mono font-black text-theme-main">{gc.code}</td>
                        <td className="px-6 py-4 font-black text-brand">${parseFloat(gc.balance).toFixed(2)}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${gc.status === 'ACTIVE' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                            {gc.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-theme-muted text-xs font-bold">{new Date(gc.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'customers':
        return <CustomerManagement />;
      case 'staff-insights':
        return <StaffInsights />;
      case 'data':
        return (
          <div className="space-y-8 animate-fade-in-up">
            <div className="bg-theme-card rounded-2xl shadow-soft border border-theme p-6 sm:p-8">
              <div className="border-b border-theme pb-5 mb-6">
                <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight"><Database size={24} className="text-brand" /> Data Management</h3>
                <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">Backup and restore your system database.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Backup Card */}
                <div className="p-8 rounded-3xl bg-theme-main/10 border border-theme flex flex-col items-center text-center group hover:bg-brand/5 hover:border-brand/30 transition-all duration-500">
                  <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-500/10">
                    <Download size={40} />
                  </div>
                  <h4 className="text-xl font-black text-theme-main uppercase tracking-tight mb-2">Create Backup</h4>
                  <p className="text-xs font-bold text-theme-muted uppercase tracking-widest leading-relaxed mb-8 max-w-[240px]">
                    Download a full snapshot of your current database including all sales, products, and customers.
                  </p>
                  <button 
                    onClick={handleBackup}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-3"
                  >
                    <Download size={18} /> Download .DB File
                  </button>
                </div>

                {/* Restore Card */}
                <div className="p-8 rounded-3xl bg-theme-main/10 border border-theme flex flex-col items-center text-center group hover:bg-rose-500/5 hover:border-rose-500/30 transition-all duration-500">
                  <div className="w-20 h-20 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-rose-500/10">
                    <RefreshCw size={40} />
                  </div>
                  <h4 className="text-xl font-black text-theme-main uppercase tracking-tight mb-2">Restore Data</h4>
                  <p className="text-xs font-bold text-theme-muted uppercase tracking-widest leading-relaxed mb-8 max-w-[240px]">
                    Replace your current data with a previous backup file. <span className="text-rose-500 font-black">This cannot be undone.</span>
                  </p>
                  <label className="w-full bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] shadow-lg shadow-rose-500/20 transition-all flex items-center justify-center gap-3 cursor-pointer">
                    <Upload size={18} /> Upload & Restore
                    <input type="file" accept=".db" className="hidden" onChange={handleRestore} />
                  </label>
                </div>
              </div>

              {/* Automated Backups Section */}
              <div className="mt-12 pt-12 border-t border-theme">
                <div className="mb-8">
                  <h3 className="text-xl font-black text-theme-main flex items-center gap-2 uppercase tracking-tight"><Clock size={24} className="text-brand" /> Automated Backups</h3>
                  <p className="text-theme-muted text-xs mt-1 font-bold uppercase tracking-widest">Schedule daily snapshots to local storage or cloud.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Local Auto-Backup */}
                  <div className="bg-theme-main/5 rounded-3xl p-6 border border-theme">
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-brand/10 text-brand"><Database size={20} /></div>
                        <div>
                          <p className="text-sm font-black text-theme-main uppercase tracking-tight">Daily Local Snapshots</p>
                          <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">Stored on server • 30-day retention</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer" 
                          checked={settings.auto_backup_local === 'true'}
                          onChange={(e) => handleUpdateSettings({ auto_backup_local: e.target.checked ? 'true' : 'false' })}
                        />
                        <div className="w-11 h-6 bg-theme-main peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                      </label>
                    </div>
                    <p className="text-[10px] font-bold text-theme-muted uppercase tracking-tight leading-relaxed">
                      Automatically creates a copy of your database every day at midnight. Backups are stored in the server's <span className="text-brand font-mono">/backups/local</span> directory.
                    </p>
                  </div>

                  {/* Google Drive Auto-Backup */}
                  <div className="bg-theme-main/5 rounded-3xl p-6 border border-theme">
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-brand/10 text-brand"><Globe size={20} /></div>
                        <div>
                          <p className="text-sm font-black text-theme-main uppercase tracking-tight">Google Drive Sync</p>
                          <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">Offsite cloud storage</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer" 
                          checked={settings.auto_backup_gdrive === 'true'}
                          onChange={(e) => handleUpdateSettings({ auto_backup_gdrive: e.target.checked ? 'true' : 'false' })}
                        />
                        <div className="w-11 h-6 bg-theme-main peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                      </label>
                    </div>
                    
                    {settings.auto_backup_gdrive === 'true' && (
                      <div className="mt-6 space-y-4 animate-fade-in">
                        <div>
                          <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Google Client ID</label>
                          <input 
                            type="password"
                            value={settings.gdrive_client_id || ''}
                            onChange={e => setSettings({...settings, gdrive_client_id: e.target.value})}
                            onBlur={() => handleUpdateSettings({ gdrive_client_id: settings.gdrive_client_id })}
                            className="w-full bg-theme-main border border-theme rounded-xl py-2 px-3 text-xs text-theme-main outline-none focus:ring-1 focus:ring-brand"
                            placeholder="your-client-id.apps.googleusercontent.com"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Google Client Secret</label>
                          <input 
                            type="password"
                            value={settings.gdrive_client_secret || ''}
                            onChange={e => setSettings({...settings, gdrive_client_secret: e.target.value})}
                            onBlur={() => handleUpdateSettings({ gdrive_client_secret: settings.gdrive_client_secret })}
                            className="w-full bg-theme-main border border-theme rounded-xl py-2 px-3 text-xs text-theme-main outline-none focus:ring-1 focus:ring-brand"
                            placeholder="GOCSPX-..."
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Refresh Token</label>
                          <input 
                            type="password"
                            value={settings.gdrive_refresh_token || ''}
                            onChange={e => setSettings({...settings, gdrive_refresh_token: e.target.value})}
                            onBlur={() => handleUpdateSettings({ gdrive_refresh_token: settings.gdrive_refresh_token })}
                            className="w-full bg-theme-main border border-theme rounded-xl py-2 px-3 text-xs text-theme-main outline-none focus:ring-1 focus:ring-brand"
                            placeholder="1//..."
                          />
                        </div>
                        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                          <p className="text-[9px] font-bold text-amber-600 leading-tight uppercase">
                            Google Drive setup requires an OAuth2 refresh token. Use the Google OAuth Playground to generate one for the Drive API.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 rounded-2xl bg-amber-500/5 border border-amber-500/20 flex gap-4 items-start">
                <AlertTriangle className="text-amber-500 shrink-0" size={24} />
                <div>
                  <p className="text-xs font-black text-amber-500 uppercase tracking-widest mb-1">Important Security Note</p>
                  <p className="text-[10px] font-bold text-theme-muted leading-relaxed uppercase tracking-tight">
                    Backups contain sensitive information including user profiles and transaction history. Store them in a secure, encrypted location. Avoid sharing backup files via unencrypted channels.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up max-w-7xl mx-auto pb-12">
      <div className="mb-10">
        <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">System Administration</h2>
        <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">Manage users, roles, and global store settings.</p>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-12 gap-8 items-start">
        {/* Settings Sidebar */}
        <div className="lg:col-span-3 flex lg:flex-col overflow-x-auto pb-4 lg:pb-0 gap-2 sticky top-0 lg:top-6 bg-theme-main lg:bg-transparent z-10 w-full no-scrollbar">
          <button 
            onClick={() => setActiveTab('users')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'users' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Users size={18} /> Users
          </button>
          <button 
            onClick={() => setActiveTab('roles')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'roles' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Shield size={18} /> Roles
          </button>
          <button 
            onClick={() => setActiveTab('stores')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'stores' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Store size={18} /> Stores
          </button>
          <button 
            onClick={() => setActiveTab('security')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'security' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Lock size={18} /> Security
          </button>
          <button 
            onClick={() => setActiveTab('preferences')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'preferences' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Settings size={18} /> Branding
          </button>
          <button 
            onClick={() => setActiveTab('loyalty')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'loyalty' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Award size={18} /> Loyalty
          </button>
          <button 
            onClick={() => setActiveTab('attendance')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'attendance' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Clock size={18} /> Attendance
          </button>
          <button 
            onClick={() => setActiveTab('giftcards')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'giftcards' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Wallet size={18} /> Gift Cards
          </button>
          <button 
            onClick={() => setActiveTab('customers')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'customers' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Users size={18} /> Customers
          </button>
          <button 
            onClick={() => setActiveTab('staff-insights')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'staff-insights' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <BarChart3 size={18} /> Staff Insights
          </button>
          <button 
            onClick={() => setActiveTab('data')}
            className={`flex-none lg:w-full flex items-center gap-4 px-5 py-3 lg:py-4 font-black uppercase text-[10px] tracking-widest rounded-2xl transition-all text-left border whitespace-nowrap ${activeTab === 'data' ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-main'}`}
          >
            <Database size={18} /> Data Management
          </button>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-9">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: '$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: '$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
];

function CurrencySearch({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [prevValue, setPrevValue] = useState(null);
  
  useEffect(() => {
    if (value !== prevValue) {
       const found = CURRENCIES.find(c => c.code.toLowerCase() === value?.toLowerCase());
       setQuery(found ? `${found.code} (${found.symbol})` : (value || ''));
       setPrevValue(value);
    }
  }, [value, prevValue]);

  const filtered = query === '' 
    ? CURRENCIES 
    : CURRENCIES.filter(c => 
        c.code.toLowerCase().includes(query.toLowerCase()) || 
        c.name.toLowerCase().includes(query.toLowerCase())
      );

  return (
    <div className="relative">
      <input 
        type="text"
        className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold"
        placeholder="Type to search currency..."
        value={query}
        onChange={e => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />
      {isOpen && filtered.length > 0 && (
        <div className="absolute z-10 w-full mt-2 bg-theme-card border border-theme rounded-xl shadow-2xl max-h-60 overflow-auto custom-scrollbar">
          {filtered.map(c => (
            <button
              key={c.code}
              className="w-full text-left px-4 py-3 hover:bg-theme-main transition-colors text-theme-main font-bold flex justify-between items-center border-b border-theme last:border-0"
              onMouseDown={(e) => {
                e.preventDefault();
                setQuery(`${c.code} (${c.symbol})`);
                setIsOpen(false);
                onChange(c);
              }}
            >
              <span>{c.code} - {c.name}</span>
              <span className="text-theme-muted bg-theme-main px-2 py-1 rounded-lg text-xs">{c.symbol}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/customers').then(res => {
      setCustomers(res.data);
      setLoading(false);
    }).catch(console.error);
  }, []);

  const handleExport = () => {
    const csvData = customers.map(c => ({
      Name: c.name,
      Email: c.email || '',
      Phone: c.phone || '',
      Points: c.points || 0,
      Balance: c.balance || 0,
      'Total Spent': c.totalSpent || 0,
      Joined: new Date(c.createdAt).toLocaleDateString()
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filtered = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.phone?.includes(searchTerm) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex justify-between items-center mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" size={18} />
          <input 
            type="text" 
            placeholder="Search customers..." 
            className="w-full pl-10 pr-4 py-2.5 bg-theme-main border border-theme rounded-xl text-sm outline-none focus:ring-2 focus:ring-brand transition-all text-theme-main font-bold"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button 
          onClick={handleExport}
          className="flex items-center gap-2 bg-theme-card border border-theme text-theme-muted px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:border-brand shadow-soft transition-all"
        >
          <Download size={18} /> Export CSV
        </button>
      </div>

      <div className="bg-theme-card rounded-2xl shadow-soft border border-theme overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Loyalty Points</th>
                <th className="px-6 py-4">Lifetime Spend</th>
                <th className="px-6 py-4 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-theme-main/10 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand font-black border border-brand/20 shadow-sm">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-theme-main">{c.name}</div>
                        <div className="text-[10px] text-theme-muted font-mono uppercase">Joined {new Date(c.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-theme-main font-medium">{c.email || 'No Email'}</div>
                    <div className="text-[10px] text-theme-muted font-mono">{c.phone || 'No Phone'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Trophy size={14} className="text-amber-500" />
                      <span className="text-sm font-black text-theme-main">{c.points.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-theme-main font-bold">${(c.totalSpent || 0).toLocaleString()}</td>
                  <td className="px-6 py-4 text-right">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${c.balance > 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-theme-main text-theme-muted'}`}>
                      ${parseFloat(c.balance).toFixed(2)}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-theme-muted italic font-bold uppercase tracking-widest text-[10px]">No customers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StaffInsights() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/performance/staff').then(res => {
      setStats(res.data);
      setLoading(false);
    }).catch(console.error);
  }, []);

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-theme-card p-6 rounded-3xl border border-theme shadow-soft">
          <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Top Performer</p>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center border border-amber-500/20">
              <Trophy size={24} />
            </div>
            <div>
              <p className="text-xl font-black text-theme-main">{stats[0]?.name || '---'}</p>
              <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-tight">Best Sales Velocity</p>
            </div>
          </div>
        </div>
        <div className="bg-theme-card p-6 rounded-3xl border border-theme shadow-soft">
          <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Team Efficiency</p>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand flex items-center justify-center border border-brand/20">
              <BarChart3 size={24} />
            </div>
            <div>
              <p className="text-xl font-black text-theme-main">
                ${(stats.reduce((s, i) => s + i.avgOrder, 0) / (stats.length || 1)).toFixed(2)}
              </p>
              <p className="text-[10px] font-bold text-theme-muted uppercase tracking-tight">Avg Order Value</p>
            </div>
          </div>
        </div>
        <div className="bg-theme-card p-6 rounded-3xl border border-theme shadow-soft">
          <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Total Team Orders</p>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand flex items-center justify-center border border-brand/20">
              <ShoppingCart size={24} />
            </div>
            <div>
              <p className="text-xl font-black text-theme-main">
                {stats.reduce((s, i) => s + i.orders, 0)}
              </p>
              <p className="text-[10px] font-bold text-theme-muted uppercase tracking-tight">Monthly Volume</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-theme-card rounded-3xl shadow-soft border border-theme overflow-hidden">
        <div className="p-6 border-b border-theme bg-theme-main/10">
          <h3 className="text-sm font-black text-theme-main uppercase tracking-widest">Staff Performance Leaderboard</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                <th className="px-6 py-4">Employee</th>
                <th className="px-6 py-4 text-center">Orders</th>
                <th className="px-6 py-4 text-center">Avg Trans</th>
                <th className="px-6 py-4 text-right">Total Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {stats.map((s, idx) => (
                <tr key={idx} className="hover:bg-theme-main/10 transition-colors group">
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-theme-main border border-theme flex items-center justify-center text-sm font-black text-theme-muted group-hover:border-brand group-hover:text-brand transition-all">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-bold text-theme-main">{s.name}</div>
                        <div className="text-[10px] text-theme-muted font-black uppercase tracking-tight">Level {idx === 0 ? 'Expert' : 'Associate'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="bg-theme-main px-3 py-1 rounded-lg text-xs font-black text-theme-main border border-theme">{s.orders}</span>
                  </td>
                  <td className="px-6 py-5 text-center font-bold text-theme-muted text-sm">${s.avgOrder.toFixed(2)}</td>
                  <td className="px-6 py-5 text-right">
                    <span className="text-base font-black text-brand">${s.sales.toLocaleString()}</span>
                  </td>
                </tr>
              ))}
              {stats.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-theme-muted italic">No staff data for this period.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}